import { TACHE_PROMPTS, type TachePrompt } from '../data/tache-prompts.ts';
import { CEFR_ORDER } from '../data/candos.ts';
import {
  loadSettings, saveTaskSubmission, loadTaskSubmissions,
  type TaskSubmission,
} from '../data/stores.ts';
import type { CefrSubLevel } from '../data/sample-today.ts';

export function renderTache(): HTMLElement {
  const root = document.createElement('section');
  root.className = 'flex flex-col gap-6 max-w-3xl mx-auto';
  root.innerHTML = '<p class="text-[color:var(--color-ink-muted)] mt-12 text-center">Chargement…</p>';
  init(root);
  return root;
}

async function init(root: HTMLElement): Promise<void> {
  const [settings, submissions] = await Promise.all([loadSettings(), loadTaskSubmissions()]);

  // Pick this week's task — rotates weekly within the user's level.
  const levelIdx = CEFR_ORDER.indexOf(settings.currentLevel);
  const allowed = new Set(CEFR_ORDER.slice(0, levelIdx + 1));
  const pool = TACHE_PROMPTS.filter((p) => allowed.has(p.cefr as never));
  const weekNum = Math.floor(Date.now() / (7 * 86_400_000));
  const prompt = pool[weekNum % Math.max(1, pool.length)];

  root.innerHTML = '';
  if (!prompt) {
    root.innerHTML = '<p class="text-[color:var(--color-ink-muted)] text-center mt-12">Aucune tâche disponible.</p>';
    return;
  }
  root.append(renderHeader());
  root.append(renderPromptCard(prompt, settings.currentLevel));
  if (submissions.length > 0) root.append(renderHistory(submissions));
}

function renderHeader(): HTMLElement {
  const h = document.createElement('header');
  h.innerHTML = `
    <h1 class="text-3xl font-semibold tracking-tight">Tâche de la semaine</h1>
    <p class="text-sm text-[color:var(--color-ink-muted)] mt-1">Une tâche écrite ou orale par semaine, ancrée sur les descripteurs CEFR.</p>
  `;
  return h;
}

function renderPromptCard(prompt: TachePrompt, _level: CefrSubLevel): HTMLElement {
  const card = document.createElement('article');
  card.className = 'rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-elevated)] p-6 flex flex-col gap-4';

  const modeLabel = prompt.mode === 'writing' ? '✍️ Écrit' : '🎙️ Oral';
  card.innerHTML = `
    <div class="flex items-center justify-between gap-3">
      <h2 class="text-xl font-semibold">${prompt.title}</h2>
      <div class="flex gap-2">
        <span class="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded"
              style="background: var(--tint-rose-bg); color: var(--tint-rose-ink);">${prompt.cefr}</span>
        <span class="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded"
              style="background: var(--tint-butter-bg); color: var(--tint-butter-ink);">${modeLabel}</span>
      </div>
    </div>
    <p class="text-base">${prompt.scenario}</p>
    <p class="text-xs text-[color:var(--color-ink-muted)]">🎯 Cible&nbsp;: ${prompt.target}</p>
  `;

  const hints = document.createElement('details');
  hints.className = 'rounded-lg border border-[color:var(--color-border)] p-3 bg-[color:var(--color-surface)]';
  hints.innerHTML = `
    <summary class="text-sm font-medium cursor-pointer">💡 Vocabulaire et structures suggérés</summary>
    <ul class="mt-2 text-sm space-y-1 text-[color:var(--color-ink-muted)]">
      ${prompt.hints.map((h) => `<li>• ${h}</li>`).join('')}
    </ul>
  `;
  card.append(hints);

  const ta = document.createElement('textarea');
  ta.className = 'w-full min-h-[200px] p-4 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)] font-sans text-base resize-y';
  ta.placeholder = prompt.mode === 'writing'
    ? 'Écrivez votre réponse en norvégien ici…'
    : 'Notez les points clés que vous voulez aborder à l’oral (à supprimer après).';

  const meta = document.createElement('p');
  meta.className = 'text-xs text-[color:var(--color-ink-muted)] flex items-center justify-between';
  meta.innerHTML = '<span data-words>0 mot</span><span data-chars>0 caractère</span>';
  ta.addEventListener('input', () => {
    const t = ta.value.trim();
    const words = t.length === 0 ? 0 : t.split(/\s+/).length;
    const wEl = meta.querySelector('[data-words]')!;
    const cEl = meta.querySelector('[data-chars]')!;
    wEl.textContent = `${words} mot${words > 1 ? 's' : ''}`;
    cEl.textContent = `${ta.value.length} caractère${ta.value.length > 1 ? 's' : ''}`;
  });
  card.append(ta);
  card.append(meta);

  // Self-assessment criteria
  const critWrap = document.createElement('div');
  critWrap.className = 'rounded-lg border border-[color:var(--color-border)] p-4 bg-[color:var(--color-surface)]';
  critWrap.innerHTML = '<h3 class="text-sm font-medium mb-2">Auto-évaluation</h3>';
  const critList = document.createElement('div');
  critList.className = 'flex flex-col gap-2';
  const checkboxes: HTMLInputElement[] = [];
  for (const [i, criterion] of prompt.criteria.entries()) {
    const label = document.createElement('label');
    label.className = 'flex items-start gap-2 text-sm cursor-pointer';
    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.className = 'mt-1';
    cb.dataset.idx = String(i);
    checkboxes.push(cb);
    const span = document.createElement('span');
    span.textContent = criterion;
    label.append(cb, span);
    critList.append(label);
  }
  critWrap.append(critList);
  card.append(critWrap);

  // Submit
  const submit = document.createElement('button');
  submit.className = 'self-end px-6 py-3 rounded-lg bg-[color:var(--color-fjord-600)] text-white text-sm font-medium hover:bg-[color:var(--color-fjord-700)] transition-colors disabled:opacity-50';
  submit.textContent = 'Enregistrer la tâche';
  submit.addEventListener('click', async () => {
    if (ta.value.trim().length === 0) { ta.focus(); return; }
    submit.disabled = true;
    const submission: TaskSubmission = {
      id: `sub_${Date.now()}`,
      promptId: prompt.id,
      cefr: prompt.cefr as CefrSubLevel,
      mode: prompt.mode,
      text: ta.value,
      selfRating: prompt.criteria.map((criterion, i) => ({ criterion, met: checkboxes[i]?.checked ?? false })),
      submittedAt: new Date().toISOString(),
    };
    await saveTaskSubmission(submission);
    submit.replaceWith(renderConfirmation());
  });
  card.append(submit);
  return card;
}

function renderConfirmation(): HTMLElement {
  const div = document.createElement('div');
  div.className = 'self-end flex items-center gap-2 text-sm text-[color:var(--color-sage)]';
  div.innerHTML = '✓ Tâche enregistrée. Bien joué&nbsp;!';
  return div;
}

function renderHistory(submissions: TaskSubmission[]): HTMLElement {
  const sec = document.createElement('section');
  sec.innerHTML = '<h3 class="text-sm font-medium mb-3">Mes tâches précédentes</h3>';
  const list = document.createElement('div');
  list.className = 'flex flex-col gap-2';
  for (const s of submissions.sort((a, b) => b.submittedAt.localeCompare(a.submittedAt)).slice(0, 8)) {
    const item = document.createElement('details');
    item.className = 'rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface-elevated)] p-3';
    const date = new Date(s.submittedAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
    const metCount = s.selfRating.filter((r) => r.met).length;
    item.innerHTML = `
      <summary class="cursor-pointer text-sm flex items-center justify-between gap-2">
        <span>${date} · <span class="font-mono text-xs">${s.cefr}</span> · ${s.mode === 'writing' ? '✍️' : '🎙️'} ${TACHE_PROMPTS.find((p) => p.id === s.promptId)?.title ?? s.promptId}</span>
        <span class="text-xs text-[color:var(--color-ink-muted)]">${metCount}/${s.selfRating.length} critères</span>
      </summary>
      <pre class="mt-3 p-3 rounded bg-[color:var(--color-surface)] text-xs whitespace-pre-wrap font-sans">${s.text.replace(/</g, '&lt;')}</pre>
    `;
    list.append(item);
  }
  sec.append(list);
  return sec;
}
